import jsLogger from 'js-logger'

type Quality = {
  fps: number
  scale: number
}

// Fixed camera position matching Conversation.tsx CSS:
// absolute top-[3rem] left-5 w-28 h-44
const CAMERA_TOP_PX = 48 // 3rem
const CAMERA_LEFT_PX = 20 // left-5 = 5 * 4px
const CAMERA_WIDTH_PX = 112 // w-28 = 28 * 4px
const CAMERA_HEIGHT_PX = 176 // h-44 = 44 * 4px

function calculateCanvasSize(maxSize = 1024) {
  const vw = window.innerWidth
  const vh = window.innerHeight

  if (vw === 0 || vh === 0) {
    return { width: maxSize, height: maxSize }
  }

  let width = vw
  let height = vh

  const scale = Math.min(1, maxSize / Math.max(width, height))

  width = Math.round(width * scale)
  height = Math.round(height * scale)

  // чётные размеры для кодеков
  width = Math.floor(width / 2) * 2
  height = Math.floor(height / 2) * 2

  return { width, height }
}

function calculateCoverRect(
  containerW: number,
  containerH: number,
  videoW: number,
  videoH: number
) {
  const containerAspect = containerW / containerH
  const videoAspect = videoW / videoH

  let drawW: number
  let drawH: number

  if (videoAspect > containerAspect) {
    drawH = containerH
    drawW = drawH * videoAspect
  } else {
    drawW = containerW
    drawH = drawW / videoAspect
  }

  return {
    x: (containerW - drawW) / 2,
    y: (containerH - drawH) / 2,
    width: drawW,
    height: drawH
  }
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2)

  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + w - radius, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
  ctx.lineTo(x + w, y + h - radius)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
  ctx.lineTo(x + radius, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

class SessionCanvasMixer {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private rafId: number | null = null
  private dpr: number

  // 🔧 dynamic quality
  private fps = 30
  private scale = 1
  private frameInterval = 1000 / 30
  private lastDraw = 0
  private running = false
  private maxSize = 1024

  private avatarImage: HTMLImageElement | null = null
  private avatarVideo: HTMLVideoElement | null = null
  private backgroundColor: string

  constructor(
    private userVideo: HTMLVideoElement,
    avatarImageUrl: string | undefined,
    recordingQuality: 'low' | 'medium' | 'high' = 'high',
    backgroundColor = '#1F2937'
  ) {
    this.backgroundColor = backgroundColor
    switch (recordingQuality) {
      case 'high':
        this.maxSize = 1024
        break
      case 'medium':
        this.maxSize = 800
        break
      case 'low':
        this.maxSize = 640
    }

    if (avatarImageUrl) {
      this.avatarImage = new Image()
      this.avatarImage.crossOrigin = 'anonymous'
      this.avatarImage.src = avatarImageUrl
    }

    const { width, height } = calculateCanvasSize(this.maxSize)

    // this.dpr = window.devicePixelRatio || 1
    this.dpr = 1

    jsLogger.log('SessionCanvasMixer: canvas params:', {
      width,
      height,
      dpr: this.dpr,
      recordingQuality
    })

    this.canvas = document.createElement('canvas')
    this.canvas.width = width * this.dpr
    this.canvas.height = height * this.dpr
    this.canvas.style.width = `${width}px`
    this.canvas.style.height = `${height}px`

    const ctx = this.canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('Canvas ctx failed')
    this.ctx = ctx

    this.ctx.imageSmoothingEnabled = true
    this.ctx.imageSmoothingQuality = 'high'

    // initial scale
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
  }

  setAvatarVideo(video: HTMLVideoElement) {
    this.avatarVideo = video
  }

  // 🔥 НОВОЕ: управление качеством
  setQuality(q: Quality) {
    this.fps = q.fps
    this.scale = q.scale
    this.frameInterval = 1000 / q.fps
  }

  start() {
    if (this.running) return
    this.running = true
    this.lastDraw = 0

    const draw = (ts: number) => {
      if (!this.running) return

      this.rafId = requestAnimationFrame(draw)

      if (ts - this.lastDraw < this.frameInterval) return
      this.lastDraw = ts

      this.render()
    }

    this.rafId = requestAnimationFrame(draw)
  }

  stop() {
    this.running = false
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  private render() {
    const cssW = this.canvas.width / this.dpr
    const cssH = this.canvas.height / this.dpr

    this.ctx.save()

    // 🔑 scale влияет на сложность сцены
    this.ctx.setTransform(
      this.dpr * this.scale,
      0,
      0,
      this.dpr * this.scale,
      0,
      0
    )

    const drawW = cssW / this.scale
    const drawH = cssH / this.scale

    // === Avatar (fullscreen cover) ===
    const hasAvatarVideo =
      this.avatarVideo &&
      this.avatarVideo.videoWidth > 0 &&
      this.avatarVideo.videoHeight > 0

    if (hasAvatarVideo) {
      const r = calculateCoverRect(
        drawW,
        drawH,
        this.avatarVideo!.videoWidth,
        this.avatarVideo!.videoHeight
      )
      this.ctx.drawImage(this.avatarVideo!, r.x, r.y, r.width, r.height)
    } else if (
      this.avatarImage &&
      this.avatarImage.complete &&
      this.avatarImage.naturalWidth > 0
    ) {
      const r = calculateCoverRect(
        drawW,
        drawH,
        this.avatarImage.naturalWidth,
        this.avatarImage.naturalHeight
      )
      this.ctx.drawImage(this.avatarImage, r.x, r.y, r.width, r.height)
    } else {
      // fallback: solid background matching the UI session background
      this.ctx.fillStyle = this.backgroundColor
      this.ctx.fillRect(0, 0, drawW, drawH)
    }

    // === User camera (fixed position) ===
    if (this.userVideo.videoWidth && this.userVideo.videoHeight) {
      const vw = window.innerWidth
      const vh = window.innerHeight

      const x = (CAMERA_LEFT_PX / vw) * drawW
      const y = (CAMERA_TOP_PX / vh) * drawH
      const w = (CAMERA_WIDTH_PX / vw) * drawW
      const h = (CAMERA_HEIGHT_PX / vh) * drawH

      const radius = 8 * (drawW / vw)
      const borderWidth = 1 * (drawW / vw)

      const UPSCALE = 1.2

      const cover = calculateCoverRect(
        w * UPSCALE,
        h * UPSCALE,
        this.userVideo.videoWidth,
        this.userVideo.videoHeight
      )

      this.ctx.save()

      roundedRectPath(this.ctx, x, y, w, h, radius)
      this.ctx.clip()

      this.ctx.translate(x + w, y)
      this.ctx.scale(-1 / UPSCALE, 1 / UPSCALE)

      this.ctx.drawImage(
        this.userVideo,
        cover.x,
        cover.y,
        cover.width,
        cover.height
      )

      this.ctx.restore()

      // border
      this.ctx.save()
      this.ctx.lineWidth = borderWidth
      this.ctx.strokeStyle = '#FFFFFF'
      roundedRectPath(this.ctx, x, y, w, h, radius)
      this.ctx.stroke()
      this.ctx.restore()
    }

    this.ctx.restore()
  }

  getCanvas() {
    return this.canvas
  }
}

export default SessionCanvasMixer
